/**
 * Delivery Status Badge Component
 * Displays status indicator for raw material deliveries
 *
 * @author SAUBHAGYA Development Team
 * @since Raw Material Deliveries Module
 */

import React from 'react';
import { cn } from '@/lib/utils';

export type DeliveryStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

// New incoming delivery statuses (from mfg_incoming_delivery table)
export type IncomingDeliveryStatus = 'UPCOMING' | 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED';

interface DeliveryStatusBadgeProps {
  status: DeliveryStatus | string;
  statusName?: string;
  size?: 'sm' | 'md' | 'lg';
  showPulse?: boolean;
  className?: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; borderColor: string }> = {
  // Legacy route statuses
  DRAFT: {
    label: 'Draft',
    color: 'text-gray-700 dark:text-gray-300',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-gray-300 dark:border-gray-600',
  },
  ACTIVE: {
    label: 'Active',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-900/50',
    borderColor: 'border-blue-300 dark:border-blue-700',
  },
  PAUSED: {
    label: 'Paused',
    color: 'text-amber-700 dark:text-amber-300',
    bgColor: 'bg-amber-100 dark:bg-amber-900/50',
    borderColor: 'border-amber-300 dark:border-amber-700',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'text-purple-700 dark:text-purple-300',
    bgColor: 'bg-purple-100 dark:bg-purple-900/50',
    borderColor: 'border-purple-300 dark:border-purple-700',
  },
  COMPLETED: {
    label: 'Completed',
    color: 'text-emerald-700 dark:text-emerald-300',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/50',
    borderColor: 'border-emerald-300 dark:border-emerald-700',
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-900/50',
    borderColor: 'border-red-300 dark:border-red-700',
  },

  // New incoming delivery statuses (mfg_incoming_delivery table)
  UPCOMING: {
    label: 'Upcoming',
    color: 'text-sky-700 dark:text-sky-300',
    bgColor: 'bg-sky-100 dark:bg-sky-900/50',
    borderColor: 'border-sky-300 dark:border-sky-700',
  },
  IN_TRANSIT: {
    label: 'In Transit',
    color: 'text-indigo-700 dark:text-indigo-300',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/50',
    borderColor: 'border-indigo-300 dark:border-indigo-700',
  },
  RECEIVED: {
    label: 'Received',
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-100 dark:bg-green-900/50',
    borderColor: 'border-green-300 dark:border-green-700',
  },
};

const SIZE_CLASSES = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
  lg: 'px-3 py-1.5 text-sm',
};

export function DeliveryStatusBadge({
  status,
  statusName,
  size = 'md',
  showPulse = false,
  className,
}: DeliveryStatusBadgeProps) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  const displayLabel = statusName || config.label;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-medium rounded-full border',
        config.color,
        config.bgColor,
        config.borderColor,
        SIZE_CLASSES[size],
        className
      )}
    >
      {showPulse && (status === 'ACTIVE' || status === 'IN_PROGRESS' || status === 'UPCOMING' || status === 'IN_TRANSIT') && (
        <span className="relative flex h-2 w-2">
          <span
            className={cn(
              'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
              status === 'IN_PROGRESS' ? 'bg-purple-400' :
              status === 'IN_TRANSIT' ? 'bg-indigo-400' :
              status === 'UPCOMING' ? 'bg-sky-400' :
              'bg-blue-400'
            )}
          />
          <span
            className={cn(
              'relative inline-flex rounded-full h-2 w-2',
              status === 'IN_PROGRESS' ? 'bg-purple-500' :
              status === 'IN_TRANSIT' ? 'bg-indigo-500' :
              status === 'UPCOMING' ? 'bg-sky-500' :
              'bg-blue-500'
            )}
          />
        </span>
      )}
      {displayLabel}
    </span>
  );
}

export default DeliveryStatusBadge;
